import { useState, useEffect, useRef } from 'react';
import { getAccessToken, API_ENDPOINTS } from '../../api';
import { uploadFileToS3 } from '../../utils/s3Upload';

const EMPTY_FORM = { id: null, title: '', subtitle: '', image: '', link: '' };

function FeaturedSection() {
  const [featuredOffers, setFeaturedOffers] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [featuredForm, setFeaturedForm] = useState(EMPTY_FORM);
  const [featuredImageFile, setFeaturedImageFile] = useState(null);
  const [featuredUploading, setFeaturedUploading] = useState(false);
  const [isEditingFeatured, setIsEditingFeatured] = useState(false);
  const [savingFeatured, setSavingFeatured] = useState(false);
  const featuredFileInputRef = useRef();

  useEffect(() => {
    fetchFeaturedOffers();
  }, []);

  async function fetchFeaturedOffers() {
    setLoadingFeatured(true);
    try {
      const response = await fetch(API_ENDPOINTS.FEATURED_OFFERS);
      if (response.ok) {
        const data = await response.json();
        setFeaturedOffers(data.featuredOffers || data);
      }
    } catch (err) {
      console.error('Failed to fetch featured offers:', err);
    } finally {
      setLoadingFeatured(false);
    }
  }

  const handleFeaturedInputChange = (e) => {
    setFeaturedForm({ ...featuredForm, [e.target.name]: e.target.value });
  };

  const handleFeaturedFileChange = (e) => {
    if (e.target.files && e.target.files[0]) setFeaturedImageFile(e.target.files[0]);
  };

  const handleAddFeatured = async () => {
    if (!featuredForm.title || !featuredForm.subtitle) {
      alert('Please fill in Title and Subtitle');
      return;
    }
    let imageUrl = featuredForm.image;
    if (featuredImageFile) {
      setFeaturedUploading(true);
      try {
        imageUrl = await uploadFileToS3(featuredImageFile, 'featured');
      } catch (err) {
        alert('Image upload failed: ' + err.message);
        setFeaturedUploading(false);
        return;
      }
      setFeaturedUploading(false);
    }
    setSavingFeatured(true);
    try {
      // POST — no id in body
      const { id: _id, ...payload } = { ...featuredForm, image: imageUrl };
      const response = await fetch(API_ENDPOINTS.FEATURED_OFFERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to save (${response.status})`);
      const data = await response.json();
      setFeaturedOffers([...featuredOffers, data]);
      setFeaturedForm(EMPTY_FORM);
      setFeaturedImageFile(null);
      if (featuredFileInputRef.current) featuredFileInputRef.current.value = '';
      alert('Featured offer saved successfully!');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSavingFeatured(false);
    }
  };

  const handleEditFeatured = (offer) => {
    setFeaturedForm(offer);
    setIsEditingFeatured(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateFeatured = async () => {
    if (!featuredForm.title || !featuredForm.subtitle) {
      alert('Please fill in Title and Subtitle');
      return;
    }
    let imageUrl = featuredForm.image;
    if (featuredImageFile) {
      setFeaturedUploading(true);
      try {
        imageUrl = await uploadFileToS3(featuredImageFile, 'featured');
      } catch (err) {
        alert('Image upload failed: ' + err.message);
        setFeaturedUploading(false);
        return;
      }
      setFeaturedUploading(false);
    }
    setSavingFeatured(true);
    try {
      // PUT /{id} — id in path, full object in body
      const updated = { ...featuredForm, image: imageUrl };
      const response = await fetch(`${API_ENDPOINTS.FEATURED_OFFERS}/${updated.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify(updated),
      });
      if (!response.ok) throw new Error(`Failed to update (${response.status})`);
      const data = await response.json();
      setFeaturedOffers(featuredOffers.map(o => o.id === updated.id ? data : o));
      setFeaturedForm(EMPTY_FORM);
      setFeaturedImageFile(null);
      if (featuredFileInputRef.current) featuredFileInputRef.current.value = '';
      setIsEditingFeatured(false);
      alert('Featured offer updated successfully!');
    } catch (err) {
      alert('Failed to update: ' + err.message);
    } finally {
      setSavingFeatured(false);
    }
  };

  const handleDeleteFeatured = async (id) => {
    if (!window.confirm('Are you sure you want to delete this featured offer?')) return;
    setSavingFeatured(true);
    try {
      // DELETE /{id}
      const response = await fetch(`${API_ENDPOINTS.FEATURED_OFFERS}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAccessToken()}` },
      });
      if (!response.ok) throw new Error(`Failed to delete (${response.status})`);
      setFeaturedOffers(featuredOffers.filter(o => o.id !== id));
    } catch (err) {
      alert('Failed to delete featured offer: ' + err.message);
    } finally {
      setSavingFeatured(false);
    }
  };

  const handleCancelFeatured = () => {
    setFeaturedForm(EMPTY_FORM);
    setFeaturedImageFile(null);
    if (featuredFileInputRef.current) featuredFileInputRef.current.value = '';
    setIsEditingFeatured(false);
  };

  const inputStyle = { padding: 10, borderRadius: 8, border: '1px solid #fbbf24', fontSize: '0.95rem' };

  return (
    <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', padding: 24, borderRadius: 16, boxShadow: '0 2px 16px rgba(251,191,36,0.2)', marginBottom: 32, border: '2px solid #fbbf24' }}>
      <h2 style={{ color: '#b45309', marginBottom: 16 }}>⭐ Featured Offers (Trending Banner)</h2>
      <p style={{ color: '#92400e', fontSize: '0.9rem', marginBottom: 16 }}>
        These offers will appear in the sliding banner on the home page.
      </p>

      {/* Featured Offer Form */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 12, marginBottom: 20 }}>
        <h3 style={{ marginBottom: 12, color: '#78350f' }}>{isEditingFeatured ? 'Edit Featured Offer' : 'Add New Featured Offer'}</h3>
        <div className="admin-form-grid" style={{ gap: 12 }}>
          <input name="title" placeholder="Title * (e.g., Flipkart Big Sale)" value={featuredForm.title} onChange={handleFeaturedInputChange} style={inputStyle} />
          <input name="subtitle" placeholder="Subtitle * (e.g., Up to 80% OFF!)" value={featuredForm.subtitle} onChange={handleFeaturedInputChange} style={inputStyle} />
          <input name="link" placeholder="Link URL (https://...)" value={featuredForm.link} onChange={handleFeaturedInputChange} style={inputStyle} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" ref={featuredFileInputRef} onChange={handleFeaturedFileChange}
              style={{ padding: 8, borderRadius: 8, border: '1px solid #fbbf24', fontSize: '0.9rem' }} />
            {featuredImageFile && <span style={{ fontSize: '0.8rem', color: '#b45309' }}>Selected: {featuredImageFile.name}</span>}
            {featuredUploading && <span style={{ color: '#f59e42', fontSize: '0.85rem' }}>Uploading...</span>}
          </div>
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
          {isEditingFeatured ? (
            <>
              <button onClick={handleUpdateFeatured} disabled={savingFeatured}
                style={{ padding: '10px 20px', borderRadius: 8, background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                {savingFeatured ? 'Saving...' : 'Update Featured Offer'}
              </button>
              <button onClick={handleCancelFeatured}
                style={{ padding: '10px 20px', borderRadius: 8, background: '#e5e7eb', color: '#374151', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={handleAddFeatured} disabled={savingFeatured}
              style={{ padding: '10px 20px', borderRadius: 8, background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
              {savingFeatured ? 'Saving...' : '+ Add Featured Offer'}
            </button>
          )}
        </div>
      </div>

      {/* Featured Offers List */}
      <div style={{ background: '#fff', padding: 16, borderRadius: 12 }}>
        <h3 style={{ marginBottom: 12, color: '#78350f' }}>Current Featured Offers ({featuredOffers.length})</h3>
        {loadingFeatured && <p style={{ color: '#b45309' }}>Loading featured offers...</p>}
        {!loadingFeatured && featuredOffers.length === 0 && (
          <p style={{ textAlign: 'center', color: '#92400e' }}>No featured offers yet. Add one above!</p>
        )}
        {!loadingFeatured && featuredOffers.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {featuredOffers.map(offer => (
              <div key={offer.id} style={{ display: 'flex', alignItems: 'stretch', background: '#fffbeb', borderRadius: 12, border: '1px solid #fde68a', overflow: 'hidden' }}>
                {/* Fixed-size image — always same dimensions regardless of original */}
                <div style={{ width: 110, minWidth: 110, height: 90, background: '#fef3c7', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {offer.image ? (
                    <img
                      src={offer.image}
                      alt={offer.title}
                      onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  ) : null}
                  <div style={{
                    display: offer.image ? 'none' : 'flex',
                    width: '100%', height: '100%',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.8rem',
                  }}>
                    ⭐
                  </div>
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0, padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}>
                  <div style={{ fontWeight: 700, color: '#78350f', fontSize: '0.95rem' }}>{offer.title}</div>
                  <div style={{ fontSize: '0.83rem', color: '#b45309' }}>{offer.subtitle}</div>
                  <div style={{ fontSize: '0.72rem', color: '#92400e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{offer.link}</div>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', justifyContent: 'center', flexShrink: 0 }}>
                  <button onClick={() => handleEditFeatured(offer)}
                    style={{ padding: '6px 14px', borderRadius: 6, background: '#fbbf24', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDeleteFeatured(offer.id)} disabled={savingFeatured}
                    style={{ padding: '6px 14px', borderRadius: 6, background: '#f43f5e', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FeaturedSection;
